import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Empty, Progress, Space, Tag, Tooltip, Typography } from "antd";
import { SUGGESTION_STATUS } from "store/schemaProfile/actions";

const { Text, Paragraph } = Typography;

/**
 * Per-suggestion accept/reject review screen (PRE-2722, PRE-2898).
 *
 * Design constraints that are not stylistic:
 *   - Nothing is accepted by default. A user who clicks straight through gets
 *     today's unassisted schema (AC5).
 *   - Every suggestion has its own control. There is no all-or-nothing gate,
 *     and "accept all" is a convenience, never a default state (AC3, AC4).
 *   - Sample-derived bounds are labelled as observations, not guarantees, and
 *     ambiguous inferences are flagged rather than hidden.
 *
 * PRE-2898 replaced per-field accordions with a flat grid. Two reasons, both
 * from testing a 31-suggestion schema:
 *   - accordions made the user open and scan each field in turn, with no view
 *     of the whole set and no way to find what was left;
 *   - progress counted *accepted* suggestions, so a deliberate reject looked
 *     identical to something never looked at. Progress now counts *reviewed*
 *     (accepted or rejected), and pending is called out separately, so
 *     "nothing left to look at" is a state the user can actually reach.
 */

const CATEGORY_LABELS = {
    BASE_TYPE: "Field type",
    DATE_FORMAT: "Date format",
    NUMERIC_PRECISION: "Numeric precision",
    VALUE_RANGE: "Value range",
    LENGTH_RANGE: "Length range",
    PICKLIST_CANDIDATE: "Picklist candidate",
};

const CATEGORY_COLORS = {
    BASE_TYPE: "blue",
    DATE_FORMAT: "purple",
    NUMERIC_PRECISION: "cyan",
    VALUE_RANGE: "geekblue",
    LENGTH_RANGE: "geekblue",
    PICKLIST_CANDIDATE: "gold",
};

const STATUS_LABELS = {
    [SUGGESTION_STATUS.PENDING]: "Pending",
    [SUGGESTION_STATUS.ACCEPTED]: "Accepted",
    [SUGGESTION_STATUS.REJECTED]: "Rejected",
};

const STATUS_COLORS = {
    [SUGGESTION_STATUS.PENDING]: "default",
    [SUGGESTION_STATUS.ACCEPTED]: "green",
    [SUGGESTION_STATUS.REJECTED]: "red",
};

const FILTERS = ["ALL", SUGGESTION_STATUS.PENDING, SUGGESTION_STATUS.ACCEPTED, SUGGESTION_STATUS.REJECTED];

function confidenceLabel(confidence) {
    if (confidence >= 0.95) return { text: "High confidence", color: "green" };
    if (confidence >= 0.75) return { text: "Medium confidence", color: "orange" };
    return { text: "Low confidence", color: "red" };
}

/** Flatten the per-field proposal into grid rows, preserving column order. */
function toRows(fields, suggestionStatuses) {
    const rows = [];
    (fields || []).forEach((field) => {
        (field.suggestions || []).forEach((suggestion) => {
            rows.push({
                suggestion,
                fieldName: field.name,
                stats: field.stats,
                status: suggestionStatuses[suggestion.id] || SUGGESTION_STATUS.PENDING,
            });
        });
    });
    return rows;
}

function SuggestionGridRow({ row, onAccept, onReject, showFieldName }) {
    const { suggestion, fieldName, status } = row;
    const isAccepted = status === SUGGESTION_STATUS.ACCEPTED;
    const isRejected = status === SUGGESTION_STATUS.REJECTED;
    const confidence = confidenceLabel(suggestion.confidence);

    return (
        <tr
            className={`border-b border-slate-100 align-top ${status === SUGGESTION_STATUS.PENDING ? "" : "bg-slate-50/50"}`}
            data-testid={`suggestion-row-${suggestion.id}`}
        >
            <td className="py-2 pr-3">
                {showFieldName ? (
                    <Text strong className="text-xs">
                        {fieldName}
                    </Text>
                ) : (
                    <span className="text-xs text-slate-300">&#8942;</span>
                )}
            </td>

            <td className="py-2 pr-3">
                <Tag color={CATEGORY_COLORS[suggestion.category] || "default"}>
                    {CATEGORY_LABELS[suggestion.category] || suggestion.category}
                </Tag>
            </td>

            <td className="py-2 pr-3">
                <div className="flex flex-col gap-1">
                    <Text>{suggestion.label}</Text>
                    <Paragraph type="secondary" className="!mb-0 text-xs">
                        {suggestion.rationale}
                    </Paragraph>
                    {(suggestion.sampleDerived || suggestion.ambiguous) && (
                        <div className="flex flex-wrap gap-1">
                            {suggestion.sampleDerived && (
                                <Tooltip title="Derived from the sampled rows only. Values outside this range may exist in the full dataset.">
                                    <Tag icon={<InfoCircleOutlined />} color="default">
                                        Sample-derived
                                    </Tag>
                                </Tooltip>
                            )}
                            {suggestion.ambiguous && (
                                <Tag icon={<WarningOutlined />} color="warning">
                                    Ambiguous
                                </Tag>
                            )}
                        </div>
                    )}
                    {suggestion.category === "PICKLIST_CANDIDATE" && suggestion.detail?.distinctValues && (
                        <div className="flex flex-wrap gap-1">
                            {suggestion.detail.distinctValues.slice(0, 12).map((value) => (
                                <Tag key={value} className="!text-xs">
                                    {value}
                                </Tag>
                            ))}
                            {suggestion.detail.distinctValues.length > 12 && (
                                <Text type="secondary" className="text-xs">
                                    +{suggestion.detail.distinctValues.length - 12} more
                                </Text>
                            )}
                        </div>
                    )}
                </div>
            </td>

            <td className="py-2 pr-3 whitespace-nowrap">
                {suggestion.currentValue !== null && suggestion.currentValue !== undefined ? (
                    <Text type="secondary" className="text-xs">
                        {String(suggestion.currentValue)} &rarr; <Text code>{String(suggestion.proposedValue)}</Text>
                    </Text>
                ) : (
                    <Text code className="text-xs">
                        {String(suggestion.proposedValue)}
                    </Text>
                )}
            </td>

            <td className="py-2 pr-3 whitespace-nowrap">
                <Tag color={confidence.color}>{confidence.text}</Tag>
            </td>

            <td className="py-2 pr-3 whitespace-nowrap">
                <Tag color={STATUS_COLORS[status]} data-testid={`suggestion-status-${suggestion.id}`}>
                    {STATUS_LABELS[status]}
                </Tag>
            </td>

            <td className="py-2 whitespace-nowrap">
                <Space>
                    <Button
                        size="small"
                        type={isAccepted ? "primary" : "default"}
                        icon={<CheckCircleOutlined />}
                        onClick={() => onAccept(suggestion.id)}
                        data-testid={`suggestion-accept-${suggestion.id}`}
                    >
                        Accept
                    </Button>
                    <Button
                        size="small"
                        danger={isRejected}
                        icon={<CloseCircleOutlined />}
                        onClick={() => onReject(suggestion.id)}
                        data-testid={`suggestion-reject-${suggestion.id}`}
                    >
                        Reject
                    </Button>
                </Space>
            </td>
        </tr>
    );
}

function SchemaSuggestionReview({
    schemaProfile,
    suggestionStatuses = {},
    counts,
    statusFilter = "ALL",
    onStatusFilterChange,
    onSetSuggestionStatus,
    onSetAllSuggestionStatuses,
    onContinue,
    onCancel,
    unsupported,
}) {
    // Sampling was unavailable. Not an error: continue unassisted.
    if (unsupported) {
        return (
            <Card>
                <Alert
                    type="info"
                    showIcon
                    message="Auto-configure is not available for this source"
                    description={`${unsupported.message} You can continue and define the schema as usual.`}
                />
                <div className="mt-4 flex justify-end gap-3">
                    <Button onClick={onCancel}>Back</Button>
                    <Button type="primary" onClick={() => onContinue({ fields: null })}>
                        Continue without suggestions
                    </Button>
                </div>
            </Card>
        );
    }

    if (!schemaProfile) return null;

    const { sampleMeta, fields } = schemaProfile;
    const hasSuggestions = counts.total > 0;
    const reviewed = counts.accepted + counts.rejected;
    const reviewedPercent = counts.total > 0 ? Math.round((reviewed / counts.total) * 100) : 0;

    const allRows = toRows(fields, suggestionStatuses);
    const rows = statusFilter === "ALL" ? allRows : allRows.filter((row) => row.status === statusFilter);

    const filterCount = (filter) => {
        if (filter === "ALL") return counts.total;
        if (filter === SUGGESTION_STATUS.PENDING) return counts.pending;
        if (filter === SUGGESTION_STATUS.ACCEPTED) return counts.accepted;
        return counts.rejected;
    };

    return (
        <div className="flex flex-col gap-4" data-testid="schema-suggestion-review">
            <Alert
                type="info"
                showIcon
                message={`Analysed ${sampleMeta.rowsAnalyzed} of ${sampleMeta.rowsRequested} requested row(s) across ${sampleMeta.columnCount} column(s).`}
                description={
                    <span>
                        These are suggestions only. Nothing is applied unless you accept it, and you can change
                        everything afterwards.
                        {sampleMeta.truncated && " The source has more rows than were sampled."}
                        {sampleMeta.cappedByConnector &&
                            " This connection type limits how many rows can be sampled at once."}
                    </span>
                }
            />

            {!hasSuggestions ? (
                <Empty description="No suggestions were found for this sample. Continue to define the schema as usual." />
            ) : (
                <>
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-slate-50 p-3">
                        <Space size="middle">
                            <Text data-testid="review-progress-label">
                                <Text strong>{reviewed}</Text> of {counts.total} suggestion(s) reviewed
                            </Text>
                            <Progress
                                percent={reviewedPercent}
                                size="small"
                                style={{ width: 140 }}
                                showInfo={false}
                                data-testid="review-progress"
                            />
                            <Text type="secondary" className="text-xs" data-testid="review-breakdown">
                                {counts.accepted} accepted, {counts.rejected} rejected, {counts.pending} pending
                            </Text>
                        </Space>
                        <Space>
                            <Button
                                size="small"
                                onClick={() => onSetAllSuggestionStatuses(SUGGESTION_STATUS.ACCEPTED)}
                                data-testid="suggestion-accept-all"
                            >
                                Accept all
                            </Button>
                            <Button
                                size="small"
                                onClick={() => onSetAllSuggestionStatuses(SUGGESTION_STATUS.REJECTED)}
                                data-testid="suggestion-reject-all"
                            >
                                Reject all
                            </Button>
                        </Space>
                    </div>

                    {onStatusFilterChange && (
                        <Space size={4} data-testid="suggestion-filters">
                            {FILTERS.map((filter) => (
                                <Button
                                    key={filter}
                                    size="small"
                                    type={statusFilter === filter ? "primary" : "default"}
                                    onClick={() => onStatusFilterChange(filter)}
                                    data-testid={`suggestion-filter-${filter}`}
                                >
                                    {filter === "ALL" ? "All" : STATUS_LABELS[filter]} ({filterCount(filter)})
                                </Button>
                            ))}
                        </Space>
                    )}

                    {rows.length === 0 ? (
                        <Empty description="No suggestions match this filter." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse text-left" data-testid="suggestion-grid">
                                <thead>
                                    <tr className="border-b border-slate-200">
                                        <th className="py-2 pr-3 text-xs font-medium text-slate-500">Field</th>
                                        <th className="py-2 pr-3 text-xs font-medium text-slate-500">Category</th>
                                        <th className="py-2 pr-3 text-xs font-medium text-slate-500">Suggestion</th>
                                        <th className="py-2 pr-3 text-xs font-medium text-slate-500">Change</th>
                                        <th className="py-2 pr-3 text-xs font-medium text-slate-500">Confidence</th>
                                        <th className="py-2 pr-3 text-xs font-medium text-slate-500">Status</th>
                                        <th className="py-2 text-xs font-medium text-slate-500">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, index) => (
                                        <SuggestionGridRow
                                            key={row.suggestion.id}
                                            row={row}
                                            // Repeat the field name only when it changes, so a
                                            // field's suggestions still read as one block.
                                            showFieldName={index === 0 || rows[index - 1].fieldName !== row.fieldName}
                                            onAccept={(id) => onSetSuggestionStatus(id, SUGGESTION_STATUS.ACCEPTED)}
                                            onReject={(id) => onSetSuggestionStatus(id, SUGGESTION_STATUS.REJECTED)}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {hasSuggestions && counts.pending > 0 && (
                <Text type="secondary" className="text-xs" data-testid="review-pending-note">
                    {counts.pending} suggestion(s) not yet reviewed. Anything left pending is not applied.
                </Text>
            )}

            <div className="flex justify-end gap-3">
                <Button onClick={onCancel} data-testid="suggestion-review-cancel">
                    Cancel
                </Button>
                <Button type="primary" onClick={onContinue} data-testid="suggestion-review-continue">
                    {counts.accepted > 0
                        ? `Continue with ${counts.accepted} suggestion(s)`
                        : "Continue without suggestions"}
                </Button>
            </div>
        </div>
    );
}

export default SchemaSuggestionReview;
export { CATEGORY_LABELS, STATUS_LABELS, confidenceLabel, toRows };
